/**
 * Mock listing data for development/testing
 *
 * This data is used as a fallback when the API call to fetch a listing fails
 * (e.g., listing doesn't exist in the database yet)
 *
 * Data structure matches the Sharetribe SDK listing format
 */

import { types as sdkTypes } from '../../util/sdkLoader';

const { UUID, Money } = sdkTypes;

export const mockListings = {
  // George Lopez Comedy Show - Section 211
  '6834926c-4b9b-4a5f-a60a-6a43f71e7cea': {
    id: new UUID('6834926c-4b9b-4a5f-a60a-6a43f71e7cea'),
    type: 'listing',
    attributes: {
      title: 'George Lopez Comedy Show Tickets - Section 211, Row 16',
      description:
        "George Lopez's illustrious and multi-faceted career encompasses television, film, stand-up comedy and late-night television. Lopez has broken ground for Latino comics by embracing his ethnicity, confronting racial stereotypes and fighting for his community on and off the stage.\n\nThese are verified tickets in Section 211, Row 16, Seat 21. Great view of the stage with comfortable seating. Mobile tickets will be transferred immediately after purchase.",
      state: 'published',
      deleted: false,
      geolocation: {
        lat: 29.6847,
        lng: -95.4107,
      },
      price: new Money(5500, 'USD'),
      publicData: {
        category: 'tickets',
        listingType: 'event-ticket',
        transactionProcessAlias: 'default-purchase/release-1',
        unitType: 'item',
        eventName: 'George Lopez',
        eventDateTime: '2025-08-02T20:00:00.000Z',
        venueName: 'NRG Arena',
        venueLocation: 'One NRG Park, Houston, Texas',
        location: {
          address: 'One NRG Park, Houston, TX 77054, USA',
        },
        seatSection: '211',
        seatRow: '16',
        seatNumber: '21',
        dealType: 'Riticket Verified Fan',
        dealScore: '9.6',
        seller: 'Riticket',
        isVerified: true,
        isMobile: true,
        seatingType: 'Seated Together',
        ticketCount: 1,
        unitPrice: 55.0,
        subtotal: 55.0,
        riticketFee: 2.75,
        totalPrice: 57.75,
        deliveryMethod: 'mobile',
        instantDelivery: true,
      },
      metadata: {
        customFields: {
          eventDateTime: { schemaType: 'text' },
          venueName: { schemaType: 'text' },
          venueLocation: { schemaType: 'text' },
          seatSection: { schemaType: 'text' },
          seatRow: { schemaType: 'text' },
          seatNumber: { schemaType: 'text' },
        },
      },
    },
    author: {
      id: new UUID('mock-author-1'),
      type: 'user',
      attributes: {
        banned: false,
        deleted: false,
        profile: {
          displayName: 'Sarah Johnson',
          abbreviatedName: 'SJ',
          bio:
            "Long-time concert goer and verified ticket seller. I've been going to shows for over 10 years!",
        },
      },
    },
    images: [
      {
        id: new UUID('mock-image-1'),
        type: 'image',
        attributes: {
          variants: {
            'scaled-small': {
              url: '/images/ticket-placeholder.jpg',
              width: 400,
              height: 300,
            },
            'scaled-medium': {
              url: '/images/ticket-placeholder.jpg',
              width: 800,
              height: 600,
            },
            'scaled-large': {
              url: '/images/ticket-placeholder.jpg',
              width: 1200,
              height: 900,
            },
          },
        },
      },
    ],
    currentStock: {
      id: new UUID('mock-stock-1'),
      type: 'stock',
      attributes: {
        quantity: 1,
      },
    },
  },

  // George Lopez Premium Seating
  '683493d8-90f6-4032-88fa-a2aa16de3d60': {
    id: new UUID('683493d8-90f6-4032-88fa-a2aa16de3d60'),
    type: 'listing',
    attributes: {
      title: 'George Lopez Premium Seating Tickets - Section 102, Row 8',
      description:
        "Premium seating for George Lopez's comedy show! Get closer to the action with these verified premium seats. George Lopez's illustrious and multi-faceted career encompasses television, film, stand-up comedy and late-night television.\n\nThese premium tickets are in Section 102, Row 8, Seat 15. Excellent view, closer to the stage. Mobile tickets with instant delivery.",
      state: 'published',
      deleted: false,
      geolocation: {
        lat: 29.6847,
        lng: -95.4107,
      },
      price: new Money(8900, 'USD'),
      publicData: {
        category: 'tickets',
        listingType: 'event-ticket',
        transactionProcessAlias: 'default-purchase/release-1',
        unitType: 'item',
        eventName: 'George Lopez',
        eventDateTime: '2025-08-02T20:00:00.000Z',
        venueName: 'NRG Arena',
        venueLocation: 'One NRG Park, Houston, Texas',
        location: {
          address: 'One NRG Park, Houston, TX 77054, USA',
        },
        seatSection: '102',
        seatRow: '8',
        seatNumber: '15',
        dealType: 'Riticket Verified Fan',
        dealScore: '9.2',
        seller: 'Riticket',
        isVerified: true,
        isMobile: true,
        seatingType: 'Seated Together',
        ticketCount: 1,
        unitPrice: 89.0,
        subtotal: 89.0,
        riticketFee: 4.45,
        totalPrice: 93.45,
        deliveryMethod: 'mobile',
        instantDelivery: true,
      },
      metadata: {
        customFields: {
          eventDateTime: { schemaType: 'text' },
          venueName: { schemaType: 'text' },
          venueLocation: { schemaType: 'text' },
          seatSection: { schemaType: 'text' },
          seatRow: { schemaType: 'text' },
          seatNumber: { schemaType: 'text' },
        },
      },
    },
    author: {
      id: new UUID('mock-author-2'),
      type: 'user',
      attributes: {
        banned: false,
        deleted: false,
        profile: {
          displayName: 'Mike Chen',
          abbreviatedName: 'MC',
          bio: 'Verified seller with 100% positive feedback. Fast delivery guaranteed!',
        },
      },
    },
    images: [
      {
        id: new UUID('mock-image-2'),
        type: 'image',
        attributes: {
          variants: {
            'scaled-small': {
              url: '/images/ticket-placeholder.jpg',
              width: 400,
              height: 300,
            },
            'scaled-medium': {
              url: '/images/ticket-placeholder.jpg',
              width: 800,
              height: 600,
            },
            'scaled-large': {
              url: '/images/ticket-placeholder.jpg',
              width: 1200,
              height: 900,
            },
          },
        },
      },
    ],
    currentStock: {
      id: new UUID('mock-stock-2'),
      type: 'stock',
      attributes: {
        quantity: 1,
      },
    },
  },

  // George Lopez VIP Experience
  '6875046c-0cb6-4eb8-812c-3ccc56002dfc': {
    id: new UUID('6875046c-0cb6-4eb8-812c-3ccc56002dfc'),
    type: 'listing',
    attributes: {
      title: 'George Lopez VIP Experience Tickets - VIP Section, Row 1',
      description:
        "VIP Experience for George Lopez's comedy show! Includes premium seating, meet & greet opportunity and exclusive merchandise. George Lopez's illustrious and multi-faceted career encompasses television, film, stand-up comedy and late-night television.\n\nThese VIP tickets are in VIP Section, Row 1, Seat 12. Best seats in the house with exclusive VIP perks. Mobile tickets with instant delivery after purchase.",
      state: 'published',
      deleted: false,
      geolocation: {
        lat: 29.6847,
        lng: -95.4107,
      },
      price: new Money(12500, 'USD'),
      publicData: {
        category: 'tickets',
        listingType: 'event-ticket',
        transactionProcessAlias: 'default-purchase/release-1',
        unitType: 'item',
        eventName: 'George Lopez',
        eventDateTime: '2025-08-02T20:00:00.000Z',
        venueName: 'NRG Arena',
        venueLocation: 'One NRG Park, Houston, Texas',
        location: {
          address: 'One NRG Park, Houston, TX 77054, USA',
        },
        seatSection: 'VIP',
        seatRow: '1',
        seatNumber: '12',
        dealType: 'Riticket Verified Fan',
        dealScore: '8.8',
        seller: 'Riticket',
        isVerified: true,
        isMobile: true,
        seatingType: 'VIP Experience',
        ticketCount: 1,
        unitPrice: 125.0,
        subtotal: 125.0,
        riticketFee: 6.25,
        totalPrice: 131.25,
        deliveryMethod: 'mobile',
        instantDelivery: true,
        vipPerks: ['Meet & Greet', 'Exclusive Merchandise', 'Premium Seating'],
      },
      metadata: {
        customFields: {
          eventDateTime: { schemaType: 'text' },
          venueName: { schemaType: 'text' },
          venueLocation: { schemaType: 'text' },
          seatSection: { schemaType: 'text' },
          seatRow: { schemaType: 'text' },
          seatNumber: { schemaType: 'text' },
        },
      },
    },
    author: {
      id: new UUID('mock-author-3'),
      type: 'user',
      attributes: {
        banned: false,
        deleted: false,
        profile: {
          displayName: 'Jessica Rodriguez',
          abbreviatedName: 'JR',
          bio: 'VIP ticket specialist. Premium experiences only. Fast and secure transactions.',
        },
      },
    },
    images: [
      {
        id: new UUID('mock-image-3'),
        type: 'image',
        attributes: {
          variants: {
            'scaled-small': {
              url: '/images/ticket-placeholder.jpg',
              width: 400,
              height: 300,
            },
            'scaled-medium': {
              url: '/images/ticket-placeholder.jpg',
              width: 800,
              height: 600,
            },
            'scaled-large': {
              url: '/images/ticket-placeholder.jpg',
              width: 1200,
              height: 900,
            },
          },
        },
      },
    ],
    currentStock: {
      id: new UUID('mock-stock-3'),
      type: 'stock',
      attributes: {
        quantity: 1,
      },
    },
  },
};

/**
 * Get mock listing by ID
 * @param {UUID} listingId - The listing UUID to fetch
 * @returns {Object|null} Mock listing data or null if not found
 */
export const getMockListing = listingId => {
  const uuid = typeof listingId === 'string' ? listingId : listingId.uuid;
  return mockListings[uuid] || null;
};
